import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentsApi, OutstandingInvoice, CompletedPayment } from '../api/payments';
import { invoicesAPI } from '../api/invoices';
import toast from 'react-hot-toast';
import { formatINR, formatDate, formatDateTime, getBackendURL } from '../utils/helpers';

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash', icon: 'payments' },
  { value: 'upi', label: 'UPI', icon: 'qr_code_2' },
  { value: 'card', label: 'Card', icon: 'credit_card' },
  { value: 'bank_transfer', label: 'Bank Transfer', icon: 'account_balance' },
  { value: 'cheque', label: 'Cheque', icon: 'receipt' },
];

export default function PaymentsPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'outstanding' | 'completed'>('outstanding');
  const [search, setSearch] = useState('');
  const [completedSearch, setCompletedSearch] = useState('');
  const [completedDateFrom, setCompletedDateFrom] = useState('');
  const [completedDateTo, setCompletedDateTo] = useState('');
  const [completedMode, setCompletedMode] = useState('');

  // ── Payment Modal State ──
  const [payingInvoice, setPayingInvoice] = useState<OutstandingInvoice | null>(null);
  const [payForm, setPayForm] = useState({
    settle_amount: '',
    payment_mode: 'cash',
    reference_number: '',
    notes: '',
  });

  // ── Invoice View Modal ──
  const [viewingInvoiceId, setViewingInvoiceId] = useState<number | null>(null);

  // ── Download loading state ──
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [sharingId, setSharingId] = useState<number | null>(null);

  // ── Queries ──
  const { data: dashboard, isLoading: dashLoading } = useQuery({
    queryKey: ['payments-dashboard'],
    queryFn: () => paymentsApi.getDashboard(),
  });

  const { data: outstanding = [], isLoading: outLoading } = useQuery({
    queryKey: ['payments-outstanding', search],
    queryFn: () => paymentsApi.getOutstanding({ search: search || undefined }),
    enabled: activeTab === 'outstanding',
  });

  const { data: completedRes, isLoading: compLoading } = useQuery({
    queryKey: ['payments-completed', completedSearch, completedDateFrom, completedDateTo, completedMode],
    queryFn: () => paymentsApi.getCompleted({
      search: completedSearch || undefined,
      date_from: completedDateFrom || undefined,
      date_to: completedDateTo || undefined,
      payment_mode: completedMode || undefined,
      limit: 100,
    }),
    enabled: activeTab === 'completed',
  });
  const completed: CompletedPayment[] = completedRes?.data || [];

  // View invoice detail
  const { data: invoiceDetailRes } = useQuery({
    queryKey: ['invoice-detail', viewingInvoiceId],
    queryFn: () => invoicesAPI.getById(viewingInvoiceId!),
    enabled: !!viewingInvoiceId,
  });
  const invoiceDetail = invoiceDetailRes?.data;

  // ── Record Payment Mutation ──
  const payMutation = useMutation({
    mutationFn: () => {
      if (!payingInvoice) throw new Error('No invoice selected');
      const settleAmt = payForm.settle_amount ? Number(payForm.settle_amount) : payingInvoice.total_amount;
      return paymentsApi.recordPayment({
        invoice_id: payingInvoice.id,
        amount: settleAmt,
        settle_amount: settleAmt !== payingInvoice.total_amount ? settleAmt : undefined,
        payment_mode: payForm.payment_mode,
        reference_number: payForm.reference_number || undefined,
        notes: payForm.notes || undefined,
      });
    },
    onSuccess: () => {
      toast.success('Payment recorded successfully! Invoice settled.');
      setPayingInvoice(null);
      setPayForm({ settle_amount: '', payment_mode: 'cash', reference_number: '', notes: '' });
      queryClient.invalidateQueries({ queryKey: ['payments-outstanding'] });
      queryClient.invalidateQueries({ queryKey: ['payments-completed'] });
      queryClient.invalidateQueries({ queryKey: ['payments-dashboard'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Failed to record payment.');
    },
  });

  const openPayModal = (inv: OutstandingInvoice) => {
    setPayingInvoice(inv);
    setPayForm({
      settle_amount: String(inv.balance_due),
      payment_mode: 'cash',
      reference_number: '',
      notes: '',
    });
  };

  // ── Download Invoice PDF ──
  const handleDownloadPdf = async (invoiceId: number, invoiceCode: string) => {
    try {
      setDownloadingId(invoiceId);
      const res = await invoicesAPI.generatePdf(invoiceId);
      if (res.data?.pdf_url) {
        const pdfUrl = getBackendURL(`${res.data.pdf_url}?t=${Date.now()}`);
        const a = document.createElement('a');
        a.href = pdfUrl;
        a.target = '_blank';
        a.download = `Invoice_${invoiceCode.replace(/\//g, '-')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success('Invoice PDF downloaded!');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Failed to download PDF.');
    } finally {
      setDownloadingId(null);
    }
  };

  // ── Share via WhatsApp ──
  const handleShareWhatsApp = async (inv: OutstandingInvoice | CompletedPayment & { invoice_code: string; customer_phone: string; customer_name: string; total_amount?: number; invoice_total?: number }) => {
    const invoiceId = 'invoice_id' in inv ? inv.invoice_id : inv.id;
    const invCode = inv.invoice_code;
    const phone = inv.customer_phone;
    const name = inv.customer_name;
    const total = 'total_amount' in inv ? inv.total_amount : ('invoice_total' in inv ? inv.invoice_total : 0);

    if (!phone) {
      toast.error('Customer phone number missing.');
      return;
    }

    try {
      setSharingId(invoiceId);
      const pdfRes = await invoicesAPI.generatePdf(invoiceId);
      const pdfUrl = pdfRes.data?.pdf_url ? getBackendURL(pdfRes.data.pdf_url) : '';

      const cleanPhone = phone.replace(/\D/g, '');
      const targetPhone = cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone;
      const msg = `🙏 Greetings from *God of Ceramic Studio*!\n\nDear *${name || 'Customer'}*,\n\nHere is your Invoice *${invCode}*.\n\n💰 *Total Amount:* ₹${Number(total).toLocaleString('en-IN', { minimumFractionDigits: 2 })}\n\n🧾 *Download Invoice PDF:* ${pdfUrl}\n\nThank you for choosing GOC Studio! 🚗✨`;
      window.open(`https://wa.me/${targetPhone}?text=${encodeURIComponent(msg)}`, '_blank');
      toast.success('WhatsApp opened!');
    } catch (err: any) {
      toast.error('Failed to generate share link.');
    } finally {
      setSharingId(null);
    }
  };

  const formatPaymentMode = (mode: string) => {
    const m = PAYMENT_MODES.find(p => p.value === mode);
    return m ? m.label : mode;
  };

  const getPaymentModeIcon = (mode: string) => {
    const m = PAYMENT_MODES.find(p => p.value === mode);
    return m ? m.icon : 'payments';
  };

  return (
    <div className="space-y-6 relative z-10 max-w-7xl mx-auto">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 border-b border-white/5 pb-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-6 h-[2px] bg-performance-red"></div>
            <span className="font-label-caps text-[10px] text-performance-red tracking-[0.3em] uppercase">
              Payment Management
            </span>
          </div>
          <h1 className="font-display-hero text-2xl text-white tracking-tight">
            Payments Hub
          </h1>
          <p className="text-xs text-on-surface-variant/60">
            Manage outstanding invoices, record payments, and track collections.
          </p>
        </div>
      </div>

      {/* ── Dashboard Stats ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Outstanding */}
        <div className="glass-panel rounded-2xl p-5 bg-[#0c0c0c]/40 backdrop-blur-2xl border border-red-500/20 shadow-xl">
          <div className="flex items-center gap-2.5 mb-2">
            <span className="material-symbols-outlined text-red-400 text-[22px]">warning</span>
            <span className="text-[10px] font-label-caps text-red-400/80 tracking-wider uppercase">Outstanding</span>
          </div>
          <p className="font-display-hero text-xl text-red-300">
            {dashLoading ? '...' : formatINR(dashboard?.outstanding?.total || 0)}
          </p>
          <p className="text-[10px] text-on-surface-variant/50 mt-0.5">
            {dashboard?.outstanding?.count || 0} invoices pending
          </p>
        </div>

        {/* Today */}
        <div className="glass-panel rounded-2xl p-5 bg-[#0c0c0c]/40 backdrop-blur-2xl border border-green-500/20 shadow-xl">
          <div className="flex items-center gap-2.5 mb-2">
            <span className="material-symbols-outlined text-green-400 text-[22px]">today</span>
            <span className="text-[10px] font-label-caps text-green-400/80 tracking-wider uppercase">Today</span>
          </div>
          <p className="font-display-hero text-xl text-green-300">
            {dashLoading ? '...' : formatINR(dashboard?.today?.total || 0)}
          </p>
          <p className="text-[10px] text-on-surface-variant/50 mt-0.5">
            {dashboard?.today?.count || 0} payments
          </p>
        </div>

        {/* This Month */}
        <div className="glass-panel rounded-2xl p-5 bg-[#0c0c0c]/40 backdrop-blur-2xl border border-blue-500/20 shadow-xl">
          <div className="flex items-center gap-2.5 mb-2">
            <span className="material-symbols-outlined text-blue-400 text-[22px]">calendar_month</span>
            <span className="text-[10px] font-label-caps text-blue-400/80 tracking-wider uppercase">This Month</span>
          </div>
          <p className="font-display-hero text-xl text-blue-300">
            {dashLoading ? '...' : formatINR(dashboard?.this_month?.total || 0)}
          </p>
          <p className="text-[10px] text-on-surface-variant/50 mt-0.5">
            {dashboard?.this_month?.count || 0} payments
          </p>
        </div>

        {/* Mode Breakdown */}
        <div className="glass-panel rounded-2xl p-5 bg-[#0c0c0c]/40 backdrop-blur-2xl border border-amber-500/20 shadow-xl">
          <div className="flex items-center gap-2.5 mb-2">
            <span className="material-symbols-outlined text-amber-400 text-[22px]">pie_chart</span>
            <span className="text-[10px] font-label-caps text-amber-400/80 tracking-wider uppercase">By Mode</span>
          </div>
          <div className="space-y-1">
            {dashLoading ? (
              <p className="text-xs text-on-surface-variant/50">Loading...</p>
            ) : dashboard?.by_mode && dashboard.by_mode.length > 0 ? (
              dashboard.by_mode.slice(0, 3).map((m) => (
                <div key={m.payment_mode} className="flex justify-between text-[10px]">
                  <span className="text-on-surface-variant/60 capitalize">{formatPaymentMode(m.payment_mode)}</span>
                  <span className="text-white font-medium">{formatINR(m.total)}</span>
                </div>
              ))
            ) : (
              <p className="text-[10px] text-on-surface-variant/40">No payments this month</p>
            )}
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex gap-1 p-1 bg-white/5 rounded-xl w-fit border border-white/5">
        <button
          onClick={() => setActiveTab('outstanding')}
          className={`px-5 py-2.5 rounded-lg text-xs font-medium transition-all flex items-center gap-2 ${
            activeTab === 'outstanding'
              ? 'bg-red-500/20 text-red-300 border border-red-500/30 shadow-lg shadow-red-500/10'
              : 'text-on-surface-variant/50 hover:text-white hover:bg-white/5'
          }`}
        >
          <span className="material-symbols-outlined text-[16px]">pending_actions</span>
          Outstanding
          {outstanding.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-red-500/30 text-red-200 text-[9px] rounded-full font-bold">
              {outstanding.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('completed')}
          className={`px-5 py-2.5 rounded-lg text-xs font-medium transition-all flex items-center gap-2 ${
            activeTab === 'completed'
              ? 'bg-green-500/20 text-green-300 border border-green-500/30 shadow-lg shadow-green-500/10'
              : 'text-on-surface-variant/50 hover:text-white hover:bg-white/5'
          }`}
        >
          <span className="material-symbols-outlined text-[16px]">task_alt</span>
          Payments Done
        </button>
      </div>

      {/* ═══════════════════════════════════════════════ */}
      {/* ── TAB: OUTSTANDING ── */}
      {/* ═══════════════════════════════════════════════ */}
      {activeTab === 'outstanding' && (
        <div className="space-y-4">
          {/* Search */}
          <div className="glass-panel rounded-2xl p-4 bg-[#0c0c0c]/40 backdrop-blur-2xl border border-white/5">
            <div className="flex items-center bg-white/5 border border-white/10 focus-within:border-red-500/50 rounded-xl px-3.5 py-2.5 transition-all">
              <span className="material-symbols-outlined text-red-400/80 mr-2.5 text-[20px]">search</span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by customer name, phone, invoice code, job code, or vehicle number..."
                className="bg-transparent text-white w-full focus:outline-none text-xs placeholder:text-on-surface-variant/40"
              />
              {search && (
                <button onClick={() => setSearch('')} className="text-on-surface-variant/40 hover:text-white transition-colors">
                  <span className="material-symbols-outlined text-[16px]">cancel</span>
                </button>
              )}
            </div>
          </div>

          {/* Outstanding Table */}
          {outLoading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin w-8 h-8 border-2 border-red-400 border-t-transparent rounded-full"></div>
            </div>
          ) : outstanding.length === 0 ? (
            <div className="glass-panel rounded-2xl p-12 bg-[#0c0c0c]/40 border border-white/5 text-center">
              <span className="material-symbols-outlined text-[48px] text-green-400/30 mb-3 block">check_circle</span>
              <p className="text-on-surface-variant/60 text-sm">No outstanding invoices! All payments are settled.</p>
            </div>
          ) : (
            <div className="glass-panel rounded-2xl bg-[#0c0c0c]/40 backdrop-blur-2xl border border-white/5 shadow-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/5 bg-white/[0.02]">
                      <th className="text-left px-4 py-3 text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase">Invoice</th>
                      <th className="text-left px-4 py-3 text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase">Job Card</th>
                      <th className="text-left px-4 py-3 text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase">Customer</th>
                      <th className="text-left px-4 py-3 text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase">Vehicle</th>
                      <th className="text-right px-4 py-3 text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase">Total</th>
                      <th className="text-right px-4 py-3 text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase">Balance Due</th>
                      <th className="text-center px-4 py-3 text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase">Days</th>
                      <th className="text-center px-4 py-3 text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outstanding.map((inv) => (
                      <tr key={inv.id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                        <td className="px-4 py-3">
                          <span className="text-white font-medium">{inv.invoice_code}</span>
                          <div className="text-[10px] text-on-surface-variant/40 mt-0.5">{formatDate(inv.invoice_date)}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-blue-300 font-medium">{inv.job_code}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-white">{inv.customer_name}</span>
                          <div className="text-[10px] text-on-surface-variant/40">{inv.customer_phone}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-on-surface-variant/70">{inv.vehicle_name}</span>
                          {inv.vehicle_reg && (
                            <div className="text-[10px] text-amber-300/70 font-mono">{inv.vehicle_reg}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-white font-medium">{formatINR(inv.total_amount)}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-red-300 font-bold">{formatINR(inv.balance_due)}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            inv.days_since > 30 ? 'bg-red-500/20 text-red-300' :
                            inv.days_since > 7 ? 'bg-amber-500/20 text-amber-300' :
                            'bg-white/5 text-on-surface-variant/60'
                          }`}>
                            {inv.days_since}d
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1.5">
                            {/* Record Payment */}
                            <button
                              onClick={() => openPayModal(inv)}
                              className="px-3 py-1.5 bg-green-500/20 border border-green-500/30 text-green-300 rounded-lg text-[10px] font-bold hover:bg-green-500/30 transition-all flex items-center gap-1"
                            >
                              <span className="material-symbols-outlined text-[14px]">account_balance_wallet</span>
                              Pay
                            </button>
                            {/* View Invoice */}
                            <button
                              onClick={() => setViewingInvoiceId(inv.id)}
                              className="p-1.5 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-all"
                              title="View Invoice"
                            >
                              <span className="material-symbols-outlined text-[14px] text-blue-300">visibility</span>
                            </button>
                            {/* Download PDF */}
                            <button
                              onClick={() => handleDownloadPdf(inv.id, inv.invoice_code)}
                              disabled={downloadingId === inv.id}
                              className="p-1.5 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-all disabled:opacity-30"
                              title="Download PDF"
                            >
                              <span className={`material-symbols-outlined text-[14px] text-amber-300 ${downloadingId === inv.id ? 'animate-spin' : ''}`}>
                                {downloadingId === inv.id ? 'progress_activity' : 'download'}
                              </span>
                            </button>
                            {/* Share WhatsApp */}
                            <button
                              onClick={() => handleShareWhatsApp(inv as any)}
                              disabled={sharingId === inv.id}
                              className="p-1.5 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-all disabled:opacity-30"
                              title="Share via WhatsApp"
                            >
                              <span className={`material-symbols-outlined text-[14px] text-green-400 ${sharingId === inv.id ? 'animate-spin' : ''}`}>
                                {sharingId === inv.id ? 'progress_activity' : 'share'}
                              </span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/* ── TAB: COMPLETED PAYMENTS ── */}
      {/* ═══════════════════════════════════════════════ */}
      {activeTab === 'completed' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="glass-panel rounded-2xl p-4 bg-[#0c0c0c]/40 backdrop-blur-2xl border border-white/5 space-y-3 md:space-y-0 md:flex md:items-center md:gap-3">
            <div className="flex-1 flex items-center bg-white/5 border border-white/10 focus-within:border-green-500/50 rounded-xl px-3.5 py-2.5 transition-all">
              <span className="material-symbols-outlined text-green-400/80 mr-2.5 text-[20px]">search</span>
              <input
                value={completedSearch}
                onChange={(e) => setCompletedSearch(e.target.value)}
                placeholder="Search by customer, invoice code, job code..."
                className="bg-transparent text-white w-full focus:outline-none text-xs placeholder:text-on-surface-variant/40"
              />
            </div>
            <div className="flex items-center bg-white/5 border border-white/10 rounded-xl px-3 py-2">
              <span className="text-[10px] font-label-caps text-on-surface-variant/50 mr-2 uppercase tracking-wider">From</span>
              <input type="date" value={completedDateFrom} onChange={(e) => setCompletedDateFrom(e.target.value)} className="bg-transparent text-white text-xs outline-none" />
            </div>
            <div className="flex items-center bg-white/5 border border-white/10 rounded-xl px-3 py-2">
              <span className="text-[10px] font-label-caps text-on-surface-variant/50 mr-2 uppercase tracking-wider">To</span>
              <input type="date" value={completedDateTo} onChange={(e) => setCompletedDateTo(e.target.value)} className="bg-transparent text-white text-xs outline-none" />
            </div>
            <select
              value={completedMode}
              onChange={(e) => setCompletedMode(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white outline-none"
            >
              <option value="" className="bg-[#1a1a1a]">All Modes</option>
              {PAYMENT_MODES.map(m => (
                <option key={m.value} value={m.value} className="bg-[#1a1a1a]">{m.label}</option>
              ))}
            </select>
          </div>

          {/* Completed Table */}
          {compLoading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin w-8 h-8 border-2 border-green-400 border-t-transparent rounded-full"></div>
            </div>
          ) : completed.length === 0 ? (
            <div className="glass-panel rounded-2xl p-12 bg-[#0c0c0c]/40 border border-white/5 text-center">
              <span className="material-symbols-outlined text-[48px] text-on-surface-variant/20 mb-3 block">receipt_long</span>
              <p className="text-on-surface-variant/60 text-sm">No completed payments found.</p>
            </div>
          ) : (
            <div className="glass-panel rounded-2xl bg-[#0c0c0c]/40 backdrop-blur-2xl border border-white/5 shadow-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/5 bg-white/[0.02]">
                      <th className="text-left px-4 py-3 text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase">Date</th>
                      <th className="text-left px-4 py-3 text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase">Invoice</th>
                      <th className="text-left px-4 py-3 text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase">Job Code</th>
                      <th className="text-left px-4 py-3 text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase">Customer</th>
                      <th className="text-right px-4 py-3 text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase">Amount</th>
                      <th className="text-center px-4 py-3 text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase">Mode</th>
                      <th className="text-left px-4 py-3 text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase">Reference</th>
                      <th className="text-left px-4 py-3 text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase">Received By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completed.map((p) => (
                      <tr key={p.id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                        <td className="px-4 py-3">
                          <span className="text-white text-[11px]">{formatDateTime(p.payment_date)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-blue-300 font-medium">{p.invoice_code}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-on-surface-variant/70">{p.job_code}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-white">{p.customer_name}</span>
                          <div className="text-[10px] text-on-surface-variant/40">{p.customer_phone}</div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-green-300 font-bold">{formatINR(p.amount)}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/5 rounded-full text-[10px] text-on-surface-variant/70">
                            <span className="material-symbols-outlined text-[12px]">{getPaymentModeIcon(p.payment_mode)}</span>
                            {formatPaymentMode(p.payment_mode)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-on-surface-variant/60 font-mono text-[10px]">{p.reference_no || '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-on-surface-variant/70">{p.received_by_name || '—'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/* ── MODAL: Record Payment with Settle Amount ── */}
      {/* ═══════════════════════════════════════════════ */}
      {payingInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setPayingInvoice(null)}>
          <div className="w-full max-w-lg bg-[#111] rounded-2xl border border-white/10 shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="p-5 border-b border-white/5 bg-gradient-to-r from-green-500/10 to-transparent">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <span className="material-symbols-outlined text-green-400">account_balance_wallet</span>
                    Record Payment
                  </h2>
                  <p className="text-[10px] text-on-surface-variant/50 mt-0.5">Settle and confirm payment for this invoice</p>
                </div>
                <button onClick={() => setPayingInvoice(null)} className="p-2 hover:bg-white/5 rounded-lg transition-colors">
                  <span className="material-symbols-outlined text-on-surface-variant/40">close</span>
                </button>
              </div>
            </div>

            {/* Invoice Info */}
            <div className="p-5 space-y-4">
              <div className="bg-white/[0.03] rounded-xl p-4 border border-white/5 space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-on-surface-variant/50">Invoice</span>
                  <span className="text-blue-300 font-medium">{payingInvoice.invoice_code}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-on-surface-variant/50">Job Card</span>
                  <span className="text-white">{payingInvoice.job_code}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-on-surface-variant/50">Customer</span>
                  <span className="text-white">{payingInvoice.customer_name}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-on-surface-variant/50">Vehicle</span>
                  <span className="text-on-surface-variant/70">{payingInvoice.vehicle_name} {payingInvoice.vehicle_reg && `(${payingInvoice.vehicle_reg})`}</span>
                </div>
                <div className="border-t border-white/5 pt-2 mt-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-on-surface-variant/50">Original Total</span>
                    <span className="text-white font-bold">{formatINR(payingInvoice.total_amount)}</span>
                  </div>
                  {payingInvoice.amount_paid > 0 && (
                    <div className="flex justify-between text-xs mt-1">
                      <span className="text-on-surface-variant/50">Already Paid</span>
                      <span className="text-green-300">- {formatINR(payingInvoice.amount_paid)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-red-300 font-bold">Balance Due</span>
                    <span className="text-red-300 font-bold">{formatINR(payingInvoice.balance_due)}</span>
                  </div>
                </div>
              </div>

              {/* Settle Amount */}
              <div>
                <label className="block text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase mb-1.5">
                  Settle Amount (Final Payment) <span className="text-red-400">*</span>
                </label>
                <div className="flex items-center bg-white/5 border border-white/10 focus-within:border-green-500/50 rounded-xl px-3.5 py-2.5 transition-all">
                  <span className="text-green-400 font-bold mr-2">₹</span>
                  <input
                    type="number"
                    value={payForm.settle_amount}
                    onChange={(e) => setPayForm({ ...payForm, settle_amount: e.target.value })}
                    placeholder={String(payingInvoice.balance_due)}
                    className="bg-transparent text-white w-full focus:outline-none text-lg font-bold placeholder:text-on-surface-variant/30"
                    min={0}
                    max={payingInvoice.total_amount}
                  />
                </div>
                {Number(payForm.settle_amount) < payingInvoice.balance_due && payForm.settle_amount !== '' && (
                  <p className="text-[10px] text-amber-300 mt-1 flex items-center gap-1">
                    <span className="material-symbols-outlined text-[12px]">info</span>
                    Settling for less than original amount. Invoice will be updated to ₹{Number(payForm.settle_amount).toLocaleString('en-IN')}.
                  </p>
                )}
              </div>

              {/* Payment Mode */}
              <div>
                <label className="block text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase mb-1.5">
                  Payment Mode <span className="text-red-400">*</span>
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {PAYMENT_MODES.map((m) => (
                    <button
                      key={m.value}
                      onClick={() => setPayForm({ ...payForm, payment_mode: m.value })}
                      className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border text-[10px] transition-all ${
                        payForm.payment_mode === m.value
                          ? 'bg-green-500/20 border-green-500/40 text-green-300 shadow-lg shadow-green-500/10'
                          : 'bg-white/5 border-white/10 text-on-surface-variant/50 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <span className="material-symbols-outlined text-[18px]">{m.icon}</span>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reference Number */}
              <div>
                <label className="block text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase mb-1.5">
                  Reference / Transaction No.
                </label>
                <input
                  type="text"
                  value={payForm.reference_number}
                  onChange={(e) => setPayForm({ ...payForm, reference_number: e.target.value })}
                  placeholder="UPI Ref, Cheque No, etc."
                  className="w-full bg-white/5 border border-white/10 focus:border-green-500/50 rounded-xl px-3.5 py-2.5 text-white text-xs outline-none placeholder:text-on-surface-variant/30 transition-all"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-[10px] font-label-caps text-on-surface-variant/50 tracking-wider uppercase mb-1.5">
                  Notes
                </label>
                <textarea
                  value={payForm.notes}
                  onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                  placeholder="Optional notes..."
                  rows={2}
                  className="w-full bg-white/5 border border-white/10 focus:border-green-500/50 rounded-xl px-3.5 py-2.5 text-white text-xs outline-none placeholder:text-on-surface-variant/30 transition-all resize-none"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-white/5 flex items-center justify-between gap-3">
              <button
                onClick={() => setPayingInvoice(null)}
                className="px-5 py-2.5 text-xs text-on-surface-variant/60 hover:text-white border border-white/10 rounded-xl hover:bg-white/5 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => payMutation.mutate()}
                disabled={payMutation.isPending || !payForm.settle_amount || Number(payForm.settle_amount) <= 0}
                className="px-6 py-2.5 bg-green-500 hover:bg-green-600 disabled:bg-green-500/30 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl shadow-lg shadow-green-500/20 hover:shadow-green-500/40 transition-all flex items-center gap-2"
              >
                {payMutation.isPending ? (
                  <>
                    <span className="animate-spin material-symbols-outlined text-[16px]">progress_activity</span>
                    Processing...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-[16px]">check_circle</span>
                    Confirm Payment — {formatINR(Number(payForm.settle_amount) || 0)}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/* ── MODAL: View Invoice Detail ── */}
      {/* ═══════════════════════════════════════════════ */}
      {viewingInvoiceId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setViewingInvoiceId(null)}>
          <div className="w-full max-w-2xl bg-[#111] rounded-2xl border border-white/10 shadow-2xl overflow-hidden max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/5 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <span className="material-symbols-outlined text-blue-400">receipt_long</span>
                Invoice Details
              </h2>
              <button onClick={() => setViewingInvoiceId(null)} className="p-2 hover:bg-white/5 rounded-lg transition-colors">
                <span className="material-symbols-outlined text-on-surface-variant/40">close</span>
              </button>
            </div>

            {invoiceDetail ? (
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/[0.03] rounded-xl p-3 border border-white/5">
                    <span className="text-[10px] text-on-surface-variant/50 block">Invoice Code</span>
                    <span className="text-white font-bold">{invoiceDetail.invoice_code}</span>
                  </div>
                  <div className="bg-white/[0.03] rounded-xl p-3 border border-white/5">
                    <span className="text-[10px] text-on-surface-variant/50 block">Status</span>
                    <span className={`font-bold ${invoiceDetail.status === 'paid' ? 'text-green-300' : invoiceDetail.status === 'draft' ? 'text-amber-300' : 'text-red-300'}`}>
                      {invoiceDetail.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="bg-white/[0.03] rounded-xl p-3 border border-white/5">
                    <span className="text-[10px] text-on-surface-variant/50 block">Customer</span>
                    <span className="text-white">{invoiceDetail.customer_name}</span>
                  </div>
                  <div className="bg-white/[0.03] rounded-xl p-3 border border-white/5">
                    <span className="text-[10px] text-on-surface-variant/50 block">Date</span>
                    <span className="text-white">{formatDate(invoiceDetail.invoice_date)}</span>
                  </div>
                </div>

                {/* Items */}
                {invoiceDetail.items && invoiceDetail.items.length > 0 && (
                  <div className="bg-white/[0.03] rounded-xl border border-white/5 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-white/5">
                          <th className="text-left px-3 py-2 text-[10px] text-on-surface-variant/50">Service</th>
                          <th className="text-center px-3 py-2 text-[10px] text-on-surface-variant/50">Qty</th>
                          <th className="text-right px-3 py-2 text-[10px] text-on-surface-variant/50">Rate</th>
                          <th className="text-right px-3 py-2 text-[10px] text-on-surface-variant/50">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceDetail.items.map((item: any, idx: number) => (
                          <tr key={idx} className="border-b border-white/[0.03]">
                            <td className="px-3 py-2 text-white">{item.description}</td>
                            <td className="px-3 py-2 text-center text-on-surface-variant/70">{item.qty}</td>
                            <td className="px-3 py-2 text-right text-on-surface-variant/70">{formatINR(item.rate)}</td>
                            <td className="px-3 py-2 text-right text-white font-medium">{formatINR(item.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Totals */}
                <div className="bg-white/[0.03] rounded-xl p-4 border border-white/5 space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-on-surface-variant/50">Subtotal</span>
                    <span className="text-white">{formatINR(invoiceDetail.subtotal)}</span>
                  </div>
                  {invoiceDetail.discount_amount > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-on-surface-variant/50">Discount</span>
                      <span className="text-amber-300">- {formatINR(invoiceDetail.discount_amount)}</span>
                    </div>
                  )}
                  {invoiceDetail.apply_gst && (
                    <>
                      <div className="flex justify-between text-xs">
                        <span className="text-on-surface-variant/50">CGST ({invoiceDetail.cgst_rate}%)</span>
                        <span className="text-white">{formatINR(invoiceDetail.cgst_amount)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-on-surface-variant/50">SGST ({invoiceDetail.sgst_rate}%)</span>
                        <span className="text-white">{formatINR(invoiceDetail.sgst_amount)}</span>
                      </div>
                    </>
                  )}
                  <div className="border-t border-white/10 pt-2 flex justify-between text-sm">
                    <span className="text-white font-bold">Total</span>
                    <span className="text-white font-bold">{formatINR(invoiceDetail.total_amount)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-on-surface-variant/50">Paid</span>
                    <span className="text-green-300">{formatINR(invoiceDetail.amount_paid)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-red-300 font-bold">Balance Due</span>
                    <span className="text-red-300 font-bold">{formatINR(invoiceDetail.balance_due)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={() => { handleDownloadPdf(invoiceDetail.id, invoiceDetail.invoice_code); }}
                    className="flex-1 px-4 py-2.5 bg-blue-500/20 border border-blue-500/30 text-blue-300 rounded-xl text-xs font-bold hover:bg-blue-500/30 transition-all flex items-center justify-center gap-2"
                  >
                    <span className="material-symbols-outlined text-[16px]">download</span>
                    Download PDF
                  </button>
                  <button
                    onClick={() => setViewingInvoiceId(null)}
                    className="px-4 py-2.5 border border-white/10 text-on-surface-variant/60 rounded-xl text-xs hover:bg-white/5 transition-all"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-12 text-center">
                <div className="animate-spin w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-on-surface-variant/50 text-xs mt-3">Loading invoice...</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
